import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeftIcon,
  FileTextIcon,
  PenLineIcon,
  PlusIcon,
  ReplaceIcon,
  SaveIcon,
  Trash2Icon,
} from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import { toast } from 'sonner'

import { CORPORATE_KEY, corporateApi } from '@/api/corporate'
import { ApiError } from '@/api/errors'
import type { Minute, MinuteEffect } from '@/api/types'
import { EffectsEditor } from '@/components/corporate/effects-editor'
import { TemplateCaution } from '@/components/corporate/template-caution'
import { FormError } from '@/components/field-error'
import { Frame } from '@/components/frame'
import { Markdown } from '@/components/markdown'
import { MarkdownEditor } from '@/components/markdown-editor'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useResetOnOpen } from '@/hooks/use-reset-on-open'
import {
  blankEffect,
  BODY_LABELS,
  EFFECT_KIND_LABELS,
  effectSummary,
  MINUTE_KIND_LABELS,
  MINUTE_STATUS_LABELS,
} from '@/lib/corporate'
import { formatDateOnly, formatDateTime } from '@/lib/format'

export const Route = createFileRoute('/_app/corporate/resolutions/$minuteId')({
  component: MinutePage,
})

/** Ouvre un fichier lu avec le jeton (PDF figé, pièce) dans un nouvel onglet. */
async function openBlob(read: Promise<{ blob: Blob }>) {
  const { blob } = await read
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank', 'noopener')
  setTimeout(() => {
    URL.revokeObjectURL(url)
  }, 60_000)
}

/**
 * Une résolution ou un procès-verbal (lot 45). En brouillon : le texte, la
 * date d'effet et les effets se modifient. Adopté : il est figé, numéroté, son
 * PDF fait foi ; une correction passe par une résolution qui le remplace.
 */
function MinutePage() {
  const { minuteId } = Route.useParams()
  const minutes = useQuery({
    queryKey: [...CORPORATE_KEY, 'minutes'],
    queryFn: () => corporateApi.minutes(),
  })
  if (minutes.isPending) return <Skeleton className="h-64 w-full" />
  if (minutes.isError) return <FormError>La résolution n’a pas pu être lue.</FormError>
  const minute = minutes.data.find((item) => item.id === minuteId)
  if (!minute) return <FormError>Cette résolution n’existe pas au livre.</FormError>
  return <MinuteDetail minute={minute} />
}

function MinuteDetail({ minute }: { minute: Minute }) {
  return (
    <div className="space-y-4" data-testid="resolution-fiche">
      <Link
        to="/corporate/resolutions"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeftIcon className="size-4" aria-hidden="true" />
        Résolutions
      </Link>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold">{minute.title}</h2>
        {minute.ref && (
          <span className="font-mono text-sm text-muted-foreground">{minute.ref}</span>
        )}
        <Badge
          variant={
            minute.status === 'draft'
              ? 'warning'
              : minute.status === 'adopted'
                ? 'success'
                : 'muted'
          }
        >
          {MINUTE_STATUS_LABELS[minute.status]}
        </Badge>
      </div>
      <p className="text-sm text-muted-foreground">
        {MINUTE_KIND_LABELS[minute.kind]} des {BODY_LABELS[minute.body].toLowerCase()} · effet le{' '}
        {formatDateOnly(minute.effective_on)} · {minute.language === 'fr' ? 'français' : 'anglais'}
      </p>
      {minute.template_key && minute.status === 'draft' && <TemplateCaution />}
      {minute.status === 'draft' ? (
        <DraftEditor minute={minute} />
      ) : (
        <AdoptedView minute={minute} />
      )}
    </div>
  )
}

function DraftEditor({ minute }: { minute: Minute }) {
  const ids = {
    title: useId(),
    date: useId(),
    content: useId(),
    held: useId(),
    place: useId(),
    present: useId(),
  }
  const queryClient = useQueryClient()
  const [title, setTitle] = useState(minute.title)
  const [date, setDate] = useState(minute.effective_on)
  const [content, setContent] = useState(minute.content)
  const [effects, setEffects] = useState<MinuteEffect[]>(minute.effects)
  const [heldOn, setHeldOn] = useState(minute.meeting?.held_on ?? minute.effective_on)
  const [place, setPlace] = useState(minute.meeting?.place ?? '')
  const [present, setPresent] = useState((minute.meeting?.present ?? []).join(', '))
  const [adopting, setAdopting] = useState(false)
  useEffect(() => {
    setTitle(minute.title)
    setDate(minute.effective_on)
    setContent(minute.content)
    setEffects(minute.effects)
  }, [minute])
  // Les effets qu'un modèle inscrit d'ordinaire, s'ils manquent encore au brouillon.
  const templates = useQuery({
    queryKey: [...CORPORATE_KEY, 'templates'],
    queryFn: corporateApi.templates,
    enabled: minute.template_key !== null,
  })
  const hints = templates.data?.find((t) => t.key === minute.template_key)?.effects_hint ?? []
  const missingHints = hints.filter((kind) => !effects.some((effect) => effect.kind === kind))
  const save = useMutation({
    mutationFn: () =>
      corporateApi.updateMinute(minute.id, {
        title: title.trim(),
        effective_on: date,
        content,
        effects,
        ...(minute.kind === 'minutes'
          ? {
              meeting: {
                held_on: heldOn,
                place: place.trim() || null,
                virtual: false,
                present: present
                  .split(',')
                  .map((p) => p.trim())
                  .filter(Boolean),
                chair: minute.meeting?.chair ?? null,
                secretary: minute.meeting?.secretary ?? null,
              },
            }
          : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...CORPORATE_KEY, 'minutes'] })
      toast.success('Brouillon enregistré.')
    },
  })
  return (
    <Frame className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-[1fr_11rem]">
        <div className="space-y-1">
          <Label htmlFor={ids.title}>Titre</Label>
          <Input
            id={ids.title}
            value={title}
            onChange={(event) => {
              setTitle(event.target.value)
            }}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={ids.date}>Date d’effet</Label>
          <Input
            id={ids.date}
            type="date"
            value={date}
            onChange={(event) => {
              setDate(event.target.value)
            }}
          />
        </div>
      </div>
      {minute.kind === 'minutes' && (
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor={ids.held}>Assemblée tenue le</Label>
            <Input
              id={ids.held}
              type="date"
              value={heldOn}
              onChange={(event) => {
                setHeldOn(event.target.value)
              }}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={ids.place}>Lieu</Label>
            <Input
              id={ids.place}
              value={place}
              onChange={(event) => {
                setPlace(event.target.value)
              }}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={ids.present}>Présents (séparés par des virgules)</Label>
            <Input
              id={ids.present}
              value={present}
              onChange={(event) => {
                setPresent(event.target.value)
              }}
            />
          </div>
        </div>
      )}
      <div className="space-y-1">
        <Label htmlFor={ids.content}>Texte</Label>
        <MarkdownEditor id={ids.content} rows={14} value={content} onChange={setContent} />
      </div>
      {missingHints.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-2 rounded-md border border-info/40 bg-info/5 p-2 text-sm"
          data-testid="effets-suggeres"
        >
          <span>
            Ce modèle inscrit d’ordinaire :{' '}
            {missingHints.map((kind) => EFFECT_KIND_LABELS[kind].toLowerCase()).join(', ')}.
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              setEffects([...effects, ...missingHints.map((kind) => blankEffect(kind, date))])
            }}
          >
            Ajouter ces effets
          </Button>
        </div>
      )}
      <EffectsEditor effects={effects} effectiveOn={date} onChange={setEffects} />
      {save.error && (
        <FormError>
          {save.error instanceof ApiError && save.error.reason === 'minute_adopted'
            ? 'Cette résolution a été adoptée entre-temps : elle ne se modifie plus.'
            : save.error instanceof ApiError
              ? save.error.message
              : 'Le brouillon n’a pas pu être enregistré.'}
        </FormError>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={save.isPending || !title.trim()}
          onClick={() => {
            save.mutate()
          }}
        >
          <SaveIcon />
          Enregistrer le brouillon
        </Button>
        <Button
          type="button"
          data-testid="resolution-adopter"
          onClick={() => {
            setAdopting(true)
          }}
        >
          <PenLineIcon />
          Adopter…
        </Button>
      </div>
      <AdoptDialog minute={minute} open={adopting} onOpenChange={setAdopting} />
    </Frame>
  )
}

interface Signer {
  name: string
  capacity: string
}

function AdoptDialog({
  minute,
  open,
  onOpenChange,
}: {
  minute: Minute
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const ids = { attest: useId(), file: useId() }
  const queryClient = useQueryClient()
  const defaultCapacity = minute.body === 'shareholders' ? 'Actionnaire' : 'Administrateur'
  const [signers, setSigners] = useState<Signer[]>([{ name: '', capacity: defaultCapacity }])
  const [method, setMethod] = useState<'in_app' | 'scan'>('in_app')
  const [attest, setAttest] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  useResetOnOpen(open, minute.id, () => {
    setSigners(
      minute.signatories.length > 0
        ? minute.signatories.map((s) => ({ name: s.name, capacity: s.capacity }))
        : [{ name: '', capacity: defaultCapacity }],
    )
    setMethod('in_app')
    setAttest(false)
    setFile(null)
    adopt.reset()
  })
  const single = signers.length === 1
  const inApp = method === 'in_app' && single
  const adopt = useMutation({
    mutationFn: async () => {
      const filled = signers.map((s) => ({ name: s.name.trim(), capacity: s.capacity.trim() }))
      if (inApp) {
        return corporateApi.adoptMinute(minute.id, {
          signatories: filled,
          method: 'in_app',
          attest: true,
        })
      }
      if (!file) throw new Error('Joignez le PDF signé.')
      const scan = await corporateApi.uploadDocument({
        file,
        category: 'other',
        title: `${minute.title} — exemplaire signé`,
        effective_on: minute.effective_on,
      })
      return corporateApi.adoptMinute(minute.id, {
        signatories: filled,
        method: 'scan',
        signed_scan_document_id: scan.id,
      })
    },
    onSuccess: (adopted) => {
      void queryClient.invalidateQueries({ queryKey: CORPORATE_KEY })
      onOpenChange(false)
      toast.success(`Adoptée : ${adopted.ref}. Le PDF est figé et les registres sont à jour.`)
    },
  })
  const missing =
    adopt.error instanceof ApiError && adopt.error.reason === 'signatories_incomplete'
      ? adopt.error
      : null
  const ready =
    signers.every((s) => s.name.trim() && s.capacity.trim()) && (inApp ? attest : file !== null)
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Adopter « {minute.title} »</DialogTitle>
          <DialogDescription>
            Une fois adoptée, la résolution ne se modifie plus : elle reçoit son numéro, son PDF
            fait foi et ses effets s’inscrivent aux registres, tout d’un coup. Une résolution écrite
            exige la signature de tous les{' '}
            {minute.body === 'shareholders' ? 'actionnaires votants' : 'administrateurs'} en
            fonction à la date d’effet.
          </DialogDescription>
        </DialogHeader>
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Signataires</legend>
          {signers.map((signer, index) => (
            <div key={String(index)} className="flex gap-2">
              <Input
                aria-label="Nom du signataire"
                placeholder="Nom"
                value={signer.name}
                onChange={(event) => {
                  setSigners(
                    signers.map((s, i) => (i === index ? { ...s, name: event.target.value } : s)),
                  )
                }}
              />
              <Input
                aria-label="Qualité"
                className="w-44"
                value={signer.capacity}
                onChange={(event) => {
                  setSigners(
                    signers.map((s, i) =>
                      i === index ? { ...s, capacity: event.target.value } : s,
                    ),
                  )
                }}
              />
              {signers.length > 1 && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label="Retirer ce signataire"
                  onClick={() => {
                    setSigners(signers.filter((_, i) => i !== index))
                  }}
                >
                  <Trash2Icon />
                </Button>
              )}
            </div>
          ))}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              setSigners([...signers, { name: '', capacity: defaultCapacity }])
            }}
          >
            <PlusIcon />
            Ajouter un signataire
          </Button>
        </fieldset>
        <fieldset className="space-y-2 text-sm">
          <legend className="text-sm font-medium">Signature</legend>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name={ids.file}
              className="accent-primary"
              disabled={!single}
              checked={inApp}
              onChange={() => {
                setMethod('in_app')
              }}
            />
            Je signe dans l’application {single ? '' : '(un seul signataire seulement)'}
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name={ids.file}
              className="accent-primary"
              checked={!inApp}
              onChange={() => {
                setMethod('scan')
              }}
            />
            Joindre le PDF signé par tous
          </label>
          {inApp ? (
            <div className="flex items-start gap-2 rounded-md bg-muted/50 p-2">
              <Checkbox
                id={ids.attest}
                checked={attest}
                onCheckedChange={(checked) => {
                  setAttest(checked === true)
                }}
              />
              <Label htmlFor={ids.attest} className="font-normal">
                Je suis {signers[0]?.name.trim() ? signers[0].name.trim() : 'cette personne'} et je
                signe cette résolution.
              </Label>
            </div>
          ) : (
            <Input
              aria-label="PDF signé"
              type="file"
              accept="application/pdf"
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null)
              }}
            />
          )}
        </fieldset>
        {missing && (
          <FormError>
            Il manque des signataires en fonction à la date d’effet
            {missing.details.length > 0
              ? ` : ${missing.details.map((d) => d.msg).join(' ; ')}`
              : '.'}
          </FormError>
        )}
        {adopt.error && !missing && (
          <FormError>
            {adopt.error instanceof ApiError && adopt.error.reason === 'scan_required'
              ? 'Avec plusieurs signataires, joignez le PDF signé par tous.'
              : adopt.error.message || 'L’adoption n’a pas abouti : rien n’a été inscrit.'}
          </FormError>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            Annuler
          </Button>
          <Button
            type="button"
            disabled={!ready || adopt.isPending}
            data-testid="resolution-adopter-confirmer"
            onClick={() => {
              adopt.mutate()
            }}
          >
            Adopter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function AdoptedView({ minute }: { minute: Minute }) {
  const ids = { reason: useId() }
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [replacing, setReplacing] = useState(false)
  const [reason, setReason] = useState('')
  const pdf = useMutation({ mutationFn: () => openBlob(corporateApi.minutePdf(minute.id)) })
  const supersede = useMutation({
    mutationFn: () => corporateApi.supersedeMinute(minute.id, reason.trim()),
    onSuccess: (draft) => {
      void queryClient.invalidateQueries({ queryKey: [...CORPORATE_KEY, 'minutes'] })
      setReplacing(false)
      void navigate({ to: '/corporate/resolutions/$minuteId', params: { minuteId: draft.id } })
    },
  })
  return (
    <Frame className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={pdf.isPending}
          onClick={() => {
            pdf.mutate()
          }}
        >
          <FileTextIcon />
          Lire le PDF
        </Button>
        {minute.status === 'adopted' && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setReplacing(true)
            }}
          >
            <ReplaceIcon />
            Remplacer…
          </Button>
        )}
        {minute.superseded_by_id && (
          <Link
            to="/corporate/resolutions/$minuteId"
            params={{ minuteId: minute.superseded_by_id }}
            className="self-center text-sm text-primary underline-offset-2 hover:underline"
          >
            Voir la résolution qui la remplace
          </Link>
        )}
      </div>
      {pdf.error && <FormError>Le PDF n’a pas pu être lu.</FormError>}
      <p className="text-sm text-muted-foreground">
        Adoptée {minute.adopted_at ? `le ${formatDateTime(minute.adopted_at)}` : ''}
        {minute.adopted_by ? ` par ${minute.adopted_by.name}` : ''}. Le texte est figé ; une
        correction passe par une résolution qui la remplace.
      </p>
      <Markdown className="rounded-md border p-4 text-sm">{minute.content}</Markdown>
      <div className="space-y-1 text-sm">
        <h3 className="font-medium">Signataires</h3>
        <ul>
          {minute.signatories.map((signer) => (
            <li key={signer.name}>
              {signer.name}, {signer.capacity}
              {signer.signed_on ? ` — signé le ${formatDateOnly(signer.signed_on)}` : ''}
              {signer.method === 'in_app'
                ? ' dans l’application'
                : signer.method === 'scan'
                  ? ' (exemplaire signé joint)'
                  : ''}
            </li>
          ))}
        </ul>
      </div>
      {minute.effects.length > 0 && (
        <div className="space-y-1 text-sm">
          <h3 className="font-medium">Inscrit au livre</h3>
          <ul className="list-disc pl-5">
            {minute.effects.map((effect, index) => (
              <li key={`${effect.kind}-${String(index)}`}>{effectSummary(effect)}</li>
            ))}
          </ul>
        </div>
      )}
      <Dialog open={replacing} onOpenChange={setReplacing}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remplacer {minute.ref}</DialogTitle>
            <DialogDescription>
              La résolution adoptée reste au livre, marquée remplacée. Un brouillon qui y renvoie
              s’ouvre, à adopter à son tour.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor={ids.reason}>Pourquoi</Label>
            <Textarea
              id={ids.reason}
              rows={2}
              value={reason}
              onChange={(event) => {
                setReason(event.target.value)
              }}
            />
          </div>
          {supersede.error && (
            <FormError>
              {supersede.error instanceof ApiError
                ? supersede.error.message
                : 'Le remplacement n’a pas abouti.'}
            </FormError>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setReplacing(false)
              }}
            >
              Annuler
            </Button>
            <Button
              type="button"
              disabled={!reason.trim() || supersede.isPending}
              onClick={() => {
                supersede.mutate()
              }}
            >
              Ouvrir le brouillon de remplacement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Frame>
  )
}
